import "./LegacyButton.scss";

import clsx from "clsx";
import {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ForwardedRef,
  type MouseEvent,
  ReactNode,
  forwardRef,
} from "react";
import { twMerge } from "tailwind-merge";

import { LegacyButtonKind } from "./ButtonKinds";

import { LoadingDotsIcon } from "mds/icons";
import { type ComponentEventProperties, addTrackingToEvent } from "utils/amplitude";
import { Link, type LinkProps } from "utils/routing";

export type IconSizeType = "xs" | "s" | "m" | "l" | "xl";
export type IconColorType = "red" | "green" | "blue" | "orange" | "black";

// The current code is expecting every kind and prop variant be able to work together.
// If future changes do not follow this constraint (e.g. some `kinds` are only available in some `sizes`),
// we should consider integrating that property (e.g. `size`) into the `kind` property to be explicit
// on what is supported.
// TODO: Add all variations to a storybook page later.
export interface LegacyButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    ComponentEventProperties {
  children?: ReactNode;
  kind?: LegacyButtonKind;
  size?: IconSizeType;
  type?: "button" | "submit" | "reset";
  mobileFullWidth?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  // color is only implemented for primary buttons currently
  color?: IconColorType;
  // NOTE: This is no longer used, it does nothing! However, it still marks most spots in the app
  // where we use icon-only buttons, so we're leaving this in, in case we need to add icon-only
  // functionality back in the future.
  // TODO: Remove this starting May 2025 if we didn't end up needing it again.
  iconOnly?: boolean;
}

export interface LegacyLinkButtonProps
  extends AnchorHTMLAttributes<HTMLAnchorElement>,
    ComponentEventProperties {
  children?: ReactNode;
  kind?: LegacyButtonKind;
  size?: IconSizeType;
  to: LinkProps["to"];
  // Padding might be set differently if we expect an icon only to ensure the buttons are square
  mobileFullWidth?: boolean;
  fullWidth?: boolean;
  iconOnly?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  // color is only implemented for primary buttons currently
  color?: IconColorType;
}

/**
 * DEPRECATED, DO NOT USE - see SquareButton or SquareIconButton instead
 */
export const LegacyButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  LegacyLinkButtonProps | LegacyButtonProps
>(
  (
    {
      kind = "primary",
      isLoading,
      size = "m",
      type = "button",
      children,
      className,
      mobileFullWidth,
      fullWidth,
      readOnly,
      disabled,
      color,
      iconOnly,
      eventName,
      eventProperties,
      onClick,
      ...props
    },
    ref,
  ) => {
    // Note that currently the FDS button CSS classes are imported and will cause naming collisions
    // with "button-{kind}-{size}". Though the unused CSS will be removed before release (TODO), so we can change
    // the 'btn' prefix to 'button' later if we prefer matching the same prefix.
    const buttonClass = clsx(
      "btn",
      `btn-${kind}`,
      `btn-${size}`,
      `${color || "blue"}`,
      readOnly && "readonly",
      isLoading && "is-loading",
      iconOnly && "icon-only",
      mobileFullWidth
        ? "grow w-full max-w-full md:grow-0 md:w-auto md:max-w-fit"
        : fullWidth
          ? "w-full"
          : "max-w-fit",
    );

    if (color && kind !== "primary") {
      return <span>Color prop is only implemented for primary buttons</span>;
    }

    const clickHandler = (e: MouseEvent) => {
      if (isLoading) {
        return null;
      }

      addTrackingToEvent<MouseEvent>({ eventName, eventProperties, onEvent: onClick })(e);
    };

    if ("to" in props && props.to && !disabled && !readOnly) {
      return (
        <Link
          aria-label={props.title}
          className={twMerge(buttonClass, (disabled || readOnly) && "disabled", className)}
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          onClick={clickHandler}
          {...props}
        >
          {isLoading ? <LoadingDotsIcon /> : children}
        </Link>
      );
    }

    return (
      // @ts-expect-error Typescript isn't happy that `props` could be typed for `HTMLAnchorElement` instead of `HTMLButtonElement`
      <button
        // Passing an aria-label will still override this, but for IconButton,
        // the title and label are the same
        aria-label={props.title}
        className={twMerge(buttonClass, className)}
        disabled={disabled || readOnly}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        // The linter does not like `type` to be dynamically defined, though disabling for this case which seems fine.
        // https://github.com/jsx-eslint/eslint-plugin-react/issues/1555
        // eslint-disable-next-line react/button-has-type
        type={type as "button" | "submit" | "reset"}
        {...props}
        // It makes sense for the loading state to effecticely disable the button, but we don't
        // want to use the disabled prop, as it inferes with the button's styling.
        onClick={clickHandler}
      >
        {isLoading ? <LoadingDotsIcon /> : children}
      </button>
    );
  },
);

LegacyButton.displayName = "LegacyButton";
